<template>
  <v-row :class="fillHeight ? 'fill-height' : ''">
    <v-col :class="{ focused: typing, 'text-editor-max-height': setMaxHeight }" class="editor note-wrapper" cols="12">
      <div v-if="typing">
        <div class="menubar pa-2 bg-blue">
          <v-btn
            icon="ph:text-b"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.bold')"
            :class="{ 'is-active': editor.isActive('bold') }"
            @click="editor.commands.toggleBold()"
          />

          <v-btn
            icon="ph:text-italic"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.italic')"
            :class="{ 'is-active': editor.isActive('italic') }"
            @click="editor.commands.toggleItalic()"
          />

          <v-btn
            icon="ph:text-strikethrough"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.strike')"
            :class="{ 'is-active': editor.isActive('strike') }"
            @click="editor.commands.toggleStrike()"
          />

          <v-btn
            icon="ph:text-underline"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.underline')"
            :class="{ 'is-active': editor.isActive('underline') }"
            @click="editor.commands.toggleUnderline()"
          />

          <v-btn
            icon="ph:code"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.code')"
            :class="{ 'is-active': editor.isActive('code') }"
            @click="editor.commands.toggleCode()"
          />

          <v-btn
            icon="ph:paragraph"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.paragraph')"
            :class="{ 'is-active': editor.isActive('paragraph') }"
            @click="editor.commands.setParagraph()"
          />

          <v-btn
            icon="ph:text-h-one"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.heading1')"
            :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
            @click="editor.commands.toggleHeading({ level: 1 })"
          />

          <v-btn
            icon="ph:text-h-two"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.heading2')"
            :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
            @click="editor.commands.toggleHeading({ level: 2 })"
          />

          <v-btn
            icon="ph:text-h-three"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.heading3')"
            :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
            @click="editor.commands.toggleHeading({ level: 3 })"
          />

          <v-btn
            icon="ph:list-bullets"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.bulletList')"
            :class="{ 'is-active': editor.isActive('bulletList') }"
            @click="editor.commands.toggleBulletList()"
          />

          <v-btn
            icon="ph:list-numbers"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.orderList')"
            :class="{ 'is-active': editor.isActive('orderedList') }"
            @click="editor.commands.toggleOrderedList()"
          />

          <v-btn
            icon="ph:code"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.codeBlock')"
            :class="{ 'is-active': editor.isActive('codeBlock') }"
            @click="editor.commands.toggleCodeBlock()"
          />

          <v-btn
            icon="ph:minus"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.horizontalRule')"
            @click="editor.commands.setHorizontalRule"
          />

          <v-btn
            icon="ph:arrow-counter-clockwise"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.undo')"
            @click="editor.commands.undo()"
          />

          <v-btn
            icon="ph:arrow-clockwise"
            variant="text"
            density="comfortable"
            class="menubar__button mx-1"
            :title="t('textEditor.redo')"
            @click="editor.commands.redo()"
          />
        </div>
      </div>

      <div class="text-area-wrapper pr-1">
        <editor-content class="editor-content" :editor="editor" />
      </div>
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import { Editor, EditorContent } from '@tiptap/vue-3';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

withDefaults(
  defineProps<{
    editor: Editor;
    typing: boolean;
    setMaxHeight?: boolean;
    fillHeight?: boolean;
  }>(),
  {
    setMaxHeight: true,
    fillHeight: true,
  },
);
</script>

<style scoped>
.note-wrapper {
  border: 1px solid #9c9c9c;
  border-radius: 4px;
  transition: min-height 0.1s ease-out;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.text-area-wrapper {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
}

.text-editor-max-height {
  max-height: 190px;
}

.focused {
  border-color: #9c9c9c;
  min-height: 350px;
}

:deep(.editor p.is-editor-empty:first-child::before) {
  content: attr(data-empty-text);
  float: left;
  color: #aaa;
  pointer-events: none;
  height: 0;
  font-style: italic;
}

:deep(.editor-content) {
  flex: 1 1 auto;
}

:deep(.ProseMirror) {
  font-size: 1rem;
  height: 100%;
  padding: 0.5rem 0 0 0.5rem;
}

:deep(.ProseMirror:focus) {
  outline: none;
}

:deep(.ProseMirror > h1) {
  margin-bottom: 5px;
}

:deep(p) {
  margin: 0;
}

.is-active {
  background-color: #0000005c;
  border-radius: 4px;
  color: white;
}

:deep(ul),
:deep(ol) {
  padding: 0 1rem;
}
</style>
